import type { BetterFetch } from "@better-fetch/fetch";
import type { Store } from "@di-devs/better-feature/types";
import { atom } from "nanostores";
import { richmenuContracts } from "../../shared/contracts/richmenu.contracts";
import type {
	GetRichMenuResponse,
	GetRichMenuTemplatesResponse,
	RichMenu,
	RichMenuTemplate,
	SetRichMenuImageResponse,
} from "../../shared/types/richmenu.types";
import {
	createQuerySetMap,
	extractListeners,
	extractQueries,
	extractSignals,
} from "../shared/queryFactory";

// Convert contract to endpoint configs
const richmenuEndpointConfigs = {
	getRichMenuTemplates: {
		path: richmenuContracts.getRichMenuTemplates.path,
		method: richmenuContracts.getRichMenuTemplates.contract.method,
	},
	getRichMenu: {
		path: richmenuContracts.getRichMenu.path,
		method: richmenuContracts.getRichMenu.contract.method,
	},
	setRichMenuImage: {
		path: richmenuContracts.setRichMenuImage.path,
		method: richmenuContracts.setRichMenuImage.contract.method,
	},
} as const;

export function createRichmenuCorePlugin($fetch: BetterFetch, _$store?: Store) {
	const querySets = createQuerySetMap(richmenuEndpointConfigs, $fetch);

	// Define atoms for richmenu state
	const $richMenuTemplates = atom<RichMenuTemplate[]>([]);
	const $selectedRichMenu = atom<RichMenu | null>(null);
	const $isLoading = atom<boolean>(false);
	const $error = atom<string | null>(null);

	return {
		signals: extractSignals(querySets),
		queries: extractQueries(querySets),
		listeners: extractListeners(querySets),
		atoms: {
			$richMenuTemplates,
			$selectedRichMenu,
			$isLoading,
			$error,
		},
		actions: {
			async getRichMenuTemplates() {
				$isLoading.set(true);
				$error.set(null);

				try {
					const result = await $fetch<GetRichMenuTemplatesResponse>(
						richmenuContracts.getRichMenuTemplates.path,
						{
							method: richmenuContracts.getRichMenuTemplates.contract.method,
						},
					);

					// if (result?.data) {
					// 	$richMenuTemplates.set(result.data || []);
					// }

					return result;
				} catch (error) {
					const errorMessage =
						error instanceof Error
							? error.message
							: "Failed to get rich menu templates";
					$error.set(errorMessage);
					throw error;
				} finally {
					$isLoading.set(false);
				}
			},

			async getRichMenu(id: number) {
				$isLoading.set(true);
				$error.set(null);

				try {
					const result = await $fetch<GetRichMenuResponse>(
						richmenuContracts.getRichMenu.path.replace(":id", String(id)),
						{
							method: richmenuContracts.getRichMenu.contract.method,
						},
					);

					// if (result?.data) {
					// 	$selectedRichMenu.set(result.data);
					// }

					return result;
				} catch (error) {
					const errorMessage =
						error instanceof Error ? error.message : "Failed to get rich menu";
					$error.set(errorMessage);
					throw error;
				} finally {
					$isLoading.set(false);
				}
			},

			async setRichMenuImage(id: number, formData: FormData) {
				$isLoading.set(true);
				$error.set(null);

				try {
					const result = await $fetch<SetRichMenuImageResponse>(
						richmenuContracts.setRichMenuImage.path.replace(":id", String(id)),
						{
							method: richmenuContracts.setRichMenuImage.contract.method,
							body: formData,
						},
					);

					return result;
				} catch (error) {
					const errorMessage =
						error instanceof Error
							? error.message
							: "Failed to set rich menu image";
					$error.set(errorMessage);
					throw error;
				} finally {
					$isLoading.set(false);
				}
			},

			setLoading(isLoading: boolean) {
				$isLoading.set(isLoading);
			},

			setError(error: string | null) {
				$error.set(error);
			},

			selectRichMenu(richMenu: RichMenu | null) {
				$selectedRichMenu.set(richMenu);
			},
		},
		querySets,
	};
}
